import {
  AccuracyMethod,
  ComparisonBenefit,
  ComparisonEngine,
  ComparisonIncompleteReason,
  ComparisonPattern,
  IAiRemitAccuracy,
  IComparisonCell,
  IMethodAccuracy,
  IVerdictOption,
  IVerificationCell,
  IVerificationTotals,
  TruthSource,
  VerificationStatus,
} from "types/BenefitComparison/BenefitComparison";

import { formatRemitValue } from "./remitAccuracyWording";

/**
 * CLE-2820 PR 3 — every label on the Legacy vs Engine vs AI tab, in one place so the tests can pin them.
 * The UI only ever says "AI".
 */

export const COMPARISON_BENEFITS: ComparisonBenefit[] = [
  "COINSURANCE_PERCENT",
  "FLAT_COPAY",
  "PROFESSIONAL_COPAY",
  "REMAINING_DEDUCTIBLE",
  "REMAINING_OOP",
];

export const COMPARISON_BENEFIT_LABELS: Record<ComparisonBenefit, string> = {
  COINSURANCE_PERCENT: "Co-insurance",
  FLAT_COPAY: "Copay",
  PROFESSIONAL_COPAY: "Physician copay",
  REMAINING_DEDUCTIBLE: "Remaining deductible",
  REMAINING_OOP: "Remaining out-of-pocket",
};

export const COMPARISON_ENGINE_LABELS: Record<ComparisonEngine, string> = {
  QUOTED: "At time of check",
  REPLAY: "Today's config",
};

/** Said in full when the toggle is hidden, so the page never leaves which side it is showing to be guessed. */
export const COMPARISON_ENGINE_SENTENCES: Record<ComparisonEngine, string> = {
  QUOTED: "Engine: the configuration at the time of each check",
  REPLAY: "Engine: today's configuration",
};

/**
 * CLE-2820 — the engine-side toggle is offered only once "at time of check" has enough picks to be worth reading.
 *
 * 🚨 MEASURED FROM THE DATA, NEVER FROM A DATE. `QUOTED` picks only exist for checks made after the live writer
 * shipped, so before that the at-time-of-check view is almost entirely "no engine pick" — a view that answers nothing
 * and invites the reader to think the engine stopped working. The share below is of the checks under the CURRENT
 * filters, so a filter that lands on a period with picks brings the toggle back on its own.
 */
export const ENGINE_TOGGLE_MIN_COVERAGE = 0.25;

/** The share of a side's checks that have an engine pick at all; null when there are no checks to divide by. */
export const enginePickCoverage = (cells: IComparisonCell[]): number | null => {
  const checks = cells.reduce((sum, cell) => sum + cell.checks, 0);
  if (checks === 0) return null;
  const missing = cells.reduce(
    (sum, cell) => sum + cell.incompleteReasons.NO_ENGINE_PICK,
    0
  );
  return (checks - missing) / checks;
};

/** Whether the at-time-of-check side is worth offering, from its own summary's cells. */
export const engineToggleVisible = (quotedCells?: IComparisonCell[]) => {
  const coverage = enginePickCoverage(quotedCells ?? []);
  return coverage !== null && coverage >= ENGINE_TOGGLE_MIN_COVERAGE;
};

export const PATTERN_ORDER: ComparisonPattern[] = [
  "ALL_AGREE",
  "ONLY_ENGINE_DIFFERS",
  "ONLY_AI_DIFFERS",
  "ONLY_LEGACY_DIFFERS",
  "ALL_DIFFER",
];

export const PATTERN_LABELS: Record<ComparisonPattern, string> = {
  ALL_AGREE: "All three agree",
  ONLY_ENGINE_DIFFERS: "Only engine differs",
  ONLY_AI_DIFFERS: "Only AI differs",
  ONLY_LEGACY_DIFFERS: "Only legacy differs",
  ALL_DIFFER: "All differ",
};

export const INCOMPLETE_REASON_LABELS: Record<
  ComparisonIncompleteReason,
  string
> = {
  NOT_CONFIGURED: "Not configured",
  ENGINE_DECLINED: "Engine declined",
  NO_ENGINE_PICK: "No engine pick",
  NO_LEGACY_PICK: "No legacy pick",
  AI_SKIPPED: "AI skipped",
  NO_AI_PICK: "No AI pick yet",
};

/** Dollars, or a whole percent for co-insurance; "no value" is the caller's to render. */
export const formatComparisonValue = (
  value: number | null | undefined,
  benefit: ComparisonBenefit
) =>
  formatRemitValue(
    value,
    benefit === "COINSURANCE_PERCENT" ? "COINSURANCE_PERCENT" : "FLAT_COPAY"
  );

export type CheckDatePreset = "7" | "14" | "30" | "60";

export const CHECK_DATE_OPTIONS: { value: CheckDatePreset; label: string }[] = [
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
  { value: "60", label: "Last 60 days" },
];

export const checkDateRange = (
  preset: CheckDatePreset,
  today: Date = new Date()
) => ({
  checkedFrom: new Date(today.getTime() - Number(preset) * 86400000)
    .toISOString()
    .slice(0, 10),
  checkedTo: today.toISOString().slice(0, 10),
});

/**
 * "92.0% (46 / 50) · this payer · all time" — never a percentage without its sample size, and never without saying
 * that it is measured over every scored check rather than the tab's date window.
 */
export const aiRemitAccuracyText = (accuracy: IAiRemitAccuracy | null) =>
  accuracy
    ? `${((100 * accuracy.match) / accuracy.scored).toFixed(1)}% (${
        accuracy.match
      } / ${accuracy.scored}) · ${
        accuracy.scope === "PAYER" ? "this payer" : "all payers"
      } · all time`
    : "not measured against remits";

/** Too little to read anything into: shown until there are this many checks with all three sides. */
export const LITTLE_DATA_CHECKS = 50;

export const comparedChecks = (
  cells: { checks: number; incomplete: number }[]
) => cells.reduce((sum, cell) => sum + cell.checks - cell.incomplete, 0);

// ---------------------------------------------------------------------------
// CLE-2820 PR 4 — human review
// ---------------------------------------------------------------------------

/**
 * 🚨 THE FIXED QUESTION, SHOWN ON SCREEN. Every reviewer answers this one, so two reviewers — and the
 * same reviewer on two days — are judging the same thing. It must match the API's `VERDICT_QUESTION`.
 */
export const VERDICT_QUESTION =
  "Given this pVerify response and this order’s care setting, what should the estimate have quoted for this benefit?";

export const SIDE_LABELS: Record<"legacy" | "engine" | "ai", string> = {
  legacy: "Legacy",
  engine: "Engine",
  ai: "AI",
};

/** `"$75 — Legacy, Engine"`. `$0` and "nothing reported" are ONE option, and the label says so. */
export const verdictOptionLabel = (
  option: IVerdictOption,
  benefit: ComparisonBenefit
) => {
  const value =
    option.value === 0 && option.fromNoValue
      ? `${formatComparisonValue(0, benefit)} / no value`
      : formatComparisonValue(option.value, benefit);
  return `${value} — ${option.sides
    .map((side) => SIDE_LABELS[side])
    .join(", ")}`;
};

export const ACCURACY_METHOD_LABELS: Record<AccuracyMethod, string> = {
  legacy: "Legacy",
  engineAtCheck: "Engine (at time of check)",
  engineToday: "Engine (today's config)",
  ai: "AI",
};

/** "80.0% (4 / 5)" — never a percentage without its sample size; "not measured" at zero. */
export const methodAccuracyText = (accuracy: IMethodAccuracy) =>
  accuracy.scored === 0
    ? "not measured"
    : `${((100 * accuracy.match) / accuracy.scored).toFixed(1)}% (${
        accuracy.match
      } / ${accuracy.scored})`;

export const VERDICT_TYPE_LABELS = {
  VALUE: "Correct value",
  CANNOT_DETERMINE: "Can’t tell from this response",
} as const;

// ---------------------------------------------------------------------------
// CLE-2821 — verified checks, scored against today's engine
// ---------------------------------------------------------------------------

export const VERIFICATION_STATUS_LABELS: Record<VerificationStatus, string> = {
  OPEN: "Open",
  FIXED: "Fixed",
  REGRESSED: "Regressed",
  MATCHING: "Matching",
};

export const TRUTH_SOURCE_LABELS: Record<TruthSource, string> = {
  VERDICT: "Human verdict",
  REMIT: "Remit",
  BOTH: "Remit + verdict",
};

/**
 * 🚨 THE ONE SENTENCE THIS VIEW EXISTS TO SAY, and it never appears without its sample size — the
 * whole feature is a denominator.
 */
export const verifiedHeadlineText = (totals: IVerificationTotals) =>
  totals.verified === 0
    ? "No verified checks yet. A check joins this set when somebody records a verdict on it, or a remit gives it a value."
    : `Today’s engine matches ${totals.matches.toLocaleString()} of ${totals.verified.toLocaleString()} verified checks (${(
        (100 * totals.matches) /
        totals.verified
      ).toFixed(1)}%)`;

/** "8 / 10 (80.0%)" — never a percentage without its sample size; "none verified" at zero. */
export const verifiedCellText = (cell: IVerificationCell) =>
  cell.verified === 0
    ? "none verified"
    : `${cell.matches} / ${cell.verified} (${(
        (100 * cell.matches) /
        cell.verified
      ).toFixed(1)}%)`;

/** "$250" or "$100 – $250"; "no value" where the engine answered nothing. */
export const verifiedRangeText = (
  min: number | null,
  max: number | null,
  benefit: ComparisonBenefit
) => {
  if (min === null || max === null) return "no value";
  const low = formatComparisonValue(min, benefit);
  return min === max ? low : `${low} – ${formatComparisonValue(max, benefit)}`;
};

/**
 * What this view does NOT do, said on screen rather than left to be assumed: it reports, and nothing
 * here changes a configuration or a verdict.
 */
export const VERIFIED_REPORTS_ONLY_NOTE =
  "Statuses are derived by the nightly run from today’s configuration — nobody edits them, and nothing on this screen changes a configuration or a verdict.";

export const VERIFIED_NO_WINDOW_NOTE =
  "All verified checks, whatever their date: a verdict recorded months ago is the one a change is most likely to break.";
